import React, { useMemo } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  TimePicker,
  Tooltip,
  Upload,
} from 'antd'
import {
  InfoCircleOutlined,
  InboxOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import styled from 'styled-components/macro'
import ValidationErrorsModal from './ValidationErrorsModal'
import { getValidationTruncationInfo } from './validationErrors'

const BulkPriceFileEditModal = ({
  selectedPriceFileId,
  onDownloadCurrentFile,
  isDownloadingCurrentFile = false,
  hook,
}) => {
  const {
    bulkMappingFields,
    isBulkModalVisible,
    closeBulkModal,
    bulkFile,
    bulkFileHeaders,
    bulkFieldMapping,
    isMappingScanning,
    isMappingIncomplete,
    uploadProps,
    handleMappingChange,
    validationErrors,
    isValidationErrorModalVisible,
    setIsValidationErrorModalVisible,
    autoFilledFields,
    mappingLastUsedAt,
    removeMissingProducts,
    handleRemoveMissingToggle,
    projectedRemovals,
    projectedRemovalSkus,
    isDeletionListVisible,
    isFetchingDeletionSkus,
    handleToggleDeletionList,
    projectedNewProducts,
    projectedNewProductSkus,
    isNewProductListVisible,
    isFetchingNewProductSkus,
    handleToggleNewProductList,
    isBulkApplying,
    bulkProgressMessage,
    bulkApplySummary,
    handleApplyBulkChanges,
    isGuideExpanded,
    setIsGuideExpanded,
    isScheduleEnabled,
    handleScheduleToggle,
    scheduledDate,
    handleScheduledDateChange,
    scheduledTime,
    handleScheduledTimeChange,
    scheduleError,
    isScheduleConflictModalVisible,
    setIsScheduleConflictModalVisible,
    conflictingSchedule,
    handleConfirmScheduleConflict,
    resetMapping,
  } = hook

  const truncationInfo = useMemo(() => {
    if (!validationErrors.length) return null

    const lastError = validationErrors[validationErrors.length - 1]
    return (
      getValidationTruncationInfo(lastError) ||
      getValidationTruncationInfo(validationErrors.join('; '))
    )
  }, [validationErrors])

  return (
    <>
      <Modal
        title='Edit This Price File in Bulk'
        visible={isBulkModalVisible}
        onCancel={closeBulkModal}
        width={760}
        footer={[
          <Button
            key='cancel'
            onClick={closeBulkModal}
            disabled={isBulkApplying}
          >
            Cancel
          </Button>,
          <Button
            key='apply'
            type='primary'
            onClick={handleApplyBulkChanges}
            disabled={
              !bulkFile ||
              isMappingIncomplete ||
              isMappingScanning ||
              isBulkApplying ||
              validationErrors.length > 0
            }
            loading={isBulkApplying}
          >
            Apply Changes
          </Button>,
        ]}
      >
        <BulkModalContent>
          {isGuideExpanded && (
            <BulkIntroBanner>
              <BulkIntroIconWrapper>
                <InfoCircleOutlined />
              </BulkIntroIconWrapper>
              <BulkIntroBody>
                <BulkIntroContent>
                  <strong>How bulk edits work</strong>
                  <p>
                    Upload any spreadsheet format (CSV, XLSX, XLS, TSV). We only
                    need Product Title, SKU / Product Code, Size, Customer
                    Price, Type and VAT. Everything else is optional.
                  </p>
                  <p>
                    Prices in this file are for customer visibility only. You
                    still charge customers their contracted prices.
                  </p>
                </BulkIntroContent>
                <BulkIntroActions>
                  <GuideToggle onClick={() => setIsGuideExpanded(false)}>
                    Hide detailed guide
                  </GuideToggle>
                </BulkIntroActions>
              </BulkIntroBody>
            </BulkIntroBanner>
          )}
          {!isGuideExpanded && (
            <GuideToggle
              onClick={() => setIsGuideExpanded(true)}
              style={{ alignSelf: 'flex-end' }}
            >
              Show detailed guide
            </GuideToggle>
          )}

          <DownloadLinkWrapper>
            <Button
              type='link'
              onClick={onDownloadCurrentFile}
              disabled={!selectedPriceFileId || isDownloadingCurrentFile}
              loading={isDownloadingCurrentFile}
            >
              <DownloadOutlined style={{ marginRight: 6 }} />
              Download current file
            </Button>
          </DownloadLinkWrapper>

          <BulkSection>
            <BulkSectionHeader>
              <BulkSectionTitle>
                Step 1 · Upload your latest price file (we’ll map to your
                layout—no need to use the OMS format)
              </BulkSectionTitle>
            </BulkSectionHeader>
            {isGuideExpanded && (
              <BulkGuide>
                <li>
                  Upload your CSV/Excel exactly as you keep it. We scan the
                  headers automatically.
                </li>
                <li>
                  Products are matched by SKU / product code. Matching SKUs are
                  updated, new SKUs are added.
                </li>
                <li>
                  Optional: remove products you didn’t upload—use the checkbox
                  below to confirm this behaviour.
                </li>
              </BulkGuide>
            )}
            {validationErrors.length > 0 && (
              <Alert
                type='warning'
                showIcon
                message='This file has validation errors. Fix your spreadsheet, then remove and re-upload the file below.'
                style={{ marginBottom: 12 }}
              />
            )}
            <Upload.Dragger
              {...uploadProps}
              disabled={!selectedPriceFileId || isBulkApplying}
              accept='.csv,.tsv,.xls,.xlsx'
            >
              <p className='ant-upload-drag-icon'>
                <InboxOutlined />
              </p>
              <p className='ant-upload-text'>
                Drag & drop your file, or click to browse
              </p>
              <UploadTip>
                Max 20MB • Any CSV/XLS format is fine—we’ll map your headers to
                OMS fields automatically.
              </UploadTip>
            </Upload.Dragger>
          </BulkSection>

          <BulkSection>
            <BulkSectionTitle>Step 2 · Map your columns</BulkSectionTitle>
            {!bulkFile && (
              <BulkHelperText>
                Upload a file to start mapping your headers to OMS fields.
              </BulkHelperText>
            )}
            {isMappingScanning && <Spin tip='Scanning headers...' />}
            {!!bulkFile && !isMappingScanning && (
              <>
                {autoFilledFields.size > 0 && (
                  <AutoFillIndicator>
                    <span>Mappings auto-filled from previous uploads.</span>
                    {mappingLastUsedAt && (
                      <Tooltip
                        title={`Last used ${moment(
                          mappingLastUsedAt,
                        ).fromNow()}`}
                      >
                        <InfoCircleOutlined style={{ fontSize: 12 }} />
                      </Tooltip>
                    )}
                    <ResetMappingLink onClick={resetMapping}>
                      Reset mapping
                    </ResetMappingLink>
                  </AutoFillIndicator>
                )}
                <MappingGrid>
                  {bulkMappingFields.map(field => (
                    <MappingRow key={field.key}>
                      <FieldLabel>
                        {field.label}
                        {field.required && (
                          <RequiredPill>Required</RequiredPill>
                        )}
                        {autoFilledFields.has(field.key) && (
                          <AutoBadge>Auto</AutoBadge>
                        )}
                      </FieldLabel>
                      <Select
                        placeholder='Select a column'
                        value={bulkFieldMapping[field.key] || undefined}
                        onChange={value =>
                          handleMappingChange(field.key, value)
                        }
                        allowClear
                        showSearch
                      >
                        {bulkFileHeaders.map(header => (
                          <Select.Option
                            key={`${field.key}-${header}`}
                            value={header}
                          >
                            {header}
                          </Select.Option>
                        ))}
                      </Select>
                      <FieldHelper>{field.helper}</FieldHelper>
                    </MappingRow>
                  ))}
                </MappingGrid>
              </>
            )}
            {isMappingIncomplete && !!bulkFile && !isMappingScanning && (
              <Alert
                type='warning'
                showIcon
                message='Please map all required fields before continuing.'
                style={{ marginTop: 16 }}
              />
            )}
          </BulkSection>

          <BulkSection>
            <BulkSectionTitle>Step 3 · Review options</BulkSectionTitle>
            <Checkbox
              checked={removeMissingProducts}
              onChange={handleRemoveMissingToggle}
              disabled={!bulkFile || isBulkApplying}
            >
              Remove products not included in my upload
            </Checkbox>
            <BulkHelperText>
              If enabled, any product currently in this price file but missing
              from your upload will be deleted.
            </BulkHelperText>
            {removeMissingProducts && projectedRemovals !== null && (
              <Alert
                type='error'
                showIcon
                message={`You’re about to remove ${projectedRemovals} products. Double-check your file before continuing.`}
                description={
                  removeMissingProducts && projectedRemovals > 0 ? (
                    <div>
                      <Button
                        type='link'
                        onClick={handleToggleDeletionList}
                        loading={isFetchingDeletionSkus}
                      >
                        {isDeletionListVisible
                          ? 'Hide SKUs'
                          : 'View SKUs to be deleted'}
                      </Button>
                    </div>
                  ) : null
                }
                style={{ marginTop: 12 }}
              />
            )}
            {isDeletionListVisible && projectedRemovalSkus.length > 0 && (
              <DeletionList>
                <strong>SKUs scheduled for deletion</strong>
                <SkuGrid>
                  {projectedRemovalSkus.map(sku => (
                    <span key={sku}>{sku}</span>
                  ))}
                </SkuGrid>
              </DeletionList>
            )}
            {projectedNewProducts !== null && projectedNewProducts > 0 && (
              <Alert
                type='info'
                showIcon
                message={`${projectedNewProducts} new product${
                  projectedNewProducts === 1 ? '' : 's'
                } will be created from SKUs not currently in OMS.`}
                description={
                  <div>
                    <Button
                      type='link'
                      onClick={handleToggleNewProductList}
                      loading={isFetchingNewProductSkus}
                    >
                      {isNewProductListVisible
                        ? 'Hide SKUs'
                        : 'View SKUs to be created'}
                    </Button>
                  </div>
                }
                style={{ marginTop: 12 }}
              />
            )}
            {isNewProductListVisible && projectedNewProductSkus.length > 0 && (
              <DeletionList>
                <strong>New SKUs to be created</strong>
                <SkuGrid>
                  {projectedNewProductSkus.map(sku => (
                    <span key={sku}>{sku}</span>
                  ))}
                </SkuGrid>
              </DeletionList>
            )}
            <Divider />
            <BulkHelperText>
              This change will be logged under your user account for auditing so
              other supplier admins can see who updated the file.
            </BulkHelperText>
          </BulkSection>

          {bulkApplySummary && !isBulkApplying && (
            <BulkSummaryCard>
              <h4>Latest bulk edit</h4>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryLabel>Updated</SummaryLabel>
                  <SummaryValue>{bulkApplySummary.updated || 0}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Created</SummaryLabel>
                  <SummaryValue>{bulkApplySummary.created || 0}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Deleted</SummaryLabel>
                  <SummaryValue>{bulkApplySummary.deleted || 0}</SummaryValue>
                </SummaryItem>
              </SummaryGrid>
            </BulkSummaryCard>
          )}

          {isBulkApplying && (
            <ProgressBox>
              <Spin />
              <p>{bulkProgressMessage || 'Processing upload...'}</p>
              <small>
                Feel free to stay on this page—most uploads finish within a
                couple of minutes.
              </small>
            </ProgressBox>
          )}

          <Divider />

          <BulkSection>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <div>
                <BulkSectionTitle style={{ marginBottom: 4 }}>
                  Schedule this upload
                </BulkSectionTitle>
                <BulkHelperText style={{ margin: 0 }}>
                  Schedule your upload to go live at a specific date and time
                  instead of applying immediately.
                </BulkHelperText>
              </div>
              <Switch
                checked={isScheduleEnabled}
                onChange={handleScheduleToggle}
                disabled={isBulkApplying || !bulkFile}
              />
            </div>
            {isScheduleEnabled && (
              <div style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label='Date'
                      validateStatus={
                        scheduleError && !scheduledDate ? 'error' : ''
                      }
                      help={
                        scheduleError && !scheduledDate ? scheduleError : ''
                      }
                    >
                      <DatePicker
                        value={scheduledDate}
                        onChange={handleScheduledDateChange}
                        disabledDate={current => {
                          if (!current) return false
                          return current.isBefore(moment().startOf('day'))
                        }}
                        style={{ width: '100%' }}
                        format='YYYY-MM-DD'
                        disabled={isBulkApplying}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label='Time (Europe/London)'
                      validateStatus={
                        scheduleError && scheduledDate && !scheduledTime
                          ? 'error'
                          : ''
                      }
                      help={
                        scheduleError && scheduledDate && !scheduledTime
                          ? scheduleError
                          : ''
                      }
                    >
                      <TimePicker
                        value={scheduledTime}
                        onChange={handleScheduledTimeChange}
                        style={{ width: '100%' }}
                        format='HH:mm'
                        disabled={isBulkApplying}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {scheduleError && scheduledDate && scheduledTime && (
                  <Alert
                    type='error'
                    message={scheduleError}
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            )}
          </BulkSection>
        </BulkModalContent>
      </Modal>

      <ValidationErrorsModal
        visible={isValidationErrorModalVisible && validationErrors.length > 0}
        errors={validationErrors}
        truncationInfo={truncationInfo}
        onClose={() => setIsValidationErrorModalVisible(false)}
      />

      <Modal
        title='Schedule Conflict'
        visible={isScheduleConflictModalVisible}
        onCancel={() => {
          setIsScheduleConflictModalVisible(false)
        }}
        footer={[
          <Button
            key='cancel'
            onClick={() => setIsScheduleConflictModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key='continue'
            type='primary'
            onClick={handleConfirmScheduleConflict}
          >
            Continue
          </Button>,
        ]}
        width={500}
      >
        {conflictingSchedule && (
          <Alert
            type='warning'
            showIcon
            message={
              <div>
                <p style={{ marginBottom: 8 }}>
                  You already have a scheduled upload for{' '}
                  <strong>{conflictingSchedule.priceFileName}</strong> on{' '}
                  <strong>
                    {moment(conflictingSchedule.scheduledFor).format(
                      'YYYY-MM-DD HH:mm [GMT]',
                    )}
                  </strong>
                  .
                </p>
                <p style={{ marginBottom: 0 }}>
                  Scheduling this will cancel the previous one. Continue?
                </p>
              </div>
            }
          />
        )}
      </Modal>
    </>
  )
}

const BulkModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const BulkSection = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 14px;
  background: #fff;
`
const BulkSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const BulkSectionTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
`
const BulkHelperText = styled.p`
  margin: 8px 0 0;
  color: #777;
  font-size: 13px;
`
const UploadTip = styled.p`
  margin: 8px 0 0;
  color: #888;
  font-size: 12px;
`
const MappingGrid = styled.div`
  display: grid;
  gap: 14px;
`
const MappingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const FieldLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`
const FieldHelper = styled.span`
  color: #888;
  font-size: 12px;
  line-height: 1.4;
`
const AutoFillIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #2f855a;
`
const AutoBadge = styled.span`
  display: inline-block;
  background: #e6ffed;
  color: #237804;
  border: 1px solid #b7eb8f;
  border-radius: 999px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
`
const RequiredPill = styled.span`
  display: inline-block;
  background: #eef5ff;
  color: #096dd9;
  border: 1px solid #adc6ff;
  border-radius: 999px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
`
const ResetMappingLink = styled.a`
  margin-left: auto;
  cursor: pointer;
`
const DeletionList = styled.div`
  margin-top: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
`
const SkuGrid = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`
const BulkSummaryCard = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px;
  background: #fafafa;
`
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`
const SummaryItem = styled.div`
  padding: 8px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #f0f0f0;
`
const SummaryLabel = styled.div`
  color: #777;
  font-size: 12px;
`
const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 700;
`
const ProgressBox = styled.div`
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
`
const BulkIntroBanner = styled.div`
  border: 1px solid #e6f4ff;
  background: #f5fbff;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  gap: 10px;
`
const BulkIntroIconWrapper = styled.div`
  color: #1677ff;
  font-size: 16px;
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
`
const BulkIntroBody = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
`
const BulkIntroContent = styled.div`
  p {
    margin: 6px 0;
    color: #555;
  }
`
const BulkIntroActions = styled.div`
  display: flex;
  align-items: flex-start;
`
const GuideToggle = styled.a`
  cursor: pointer;
  font-size: 13px;
`
const DownloadLinkWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`
const BulkGuide = styled.ul`
  margin: 8px 0 14px;
  padding-left: 18px;
  color: #666;
  li {
    margin: 4px 0;
  }
`

export default BulkPriceFileEditModal
